import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { PlayerListTemplateV2 } from '@/components/template/player-list';
import { SvgIcon, Text } from '@/components/ui';
import { hitSlop } from '@/constants';
import { STRINGS } from '@/constants/strings';
import {
  AddTrackingButtonMobile,
  AddTrackingButtonTablet,
} from '@/features/player-session/components/overview-session/add-tracking-button';
import { overviewResetStore } from '@/features/player-session/components/overview-session/overview-card-cache-state';
import { PlayerSessionCard } from '@/features/player-session/components/overview-session/player-session-card';
import { useOverviewSessions } from '@/features/player-session/hooks/use-overview-sessions';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { createStyles, useScaling } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { HomeStackParamList } from '@/navigations/types';
import { useSessionStore } from '@/stores';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

const renderItem = ({ item }: { item: playerSession.SessionLookupData }) => (
  <PlayerSessionCard player={item} />
);

export const OverviewSessionScreen = () => {
  const userUid = useSessionStore(s => s.user?.uid);

  return (
    <PlayerListTemplateV2.Providers userUid={userUid}>
      <OverviewSessionScreenContent />
    </PlayerListTemplateV2.Providers>
  );
};

function OverviewSessionScreenContent() {
  const { scale, isTablet } = useScaling();
  const queryClient = useQueryClient();
  const styles = useStyles();
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();
  const listPlayerLogic = useOverviewSessions();
  const totalSessionRoster = listPlayerLogic?.paginatedMeta?.totalSessionRoster ?? 0;

  useEffect(() => {
    overviewResetStore.reset();
  }, [
    listPlayerLogic.page,
    listPlayerLogic.searchText,
    listPlayerLogic.sort,
    listPlayerLogic.order,
    listPlayerLogic.level,
    listPlayerLogic.lastActive,
  ]);

  const onSelectPlayer = useCallback(() => {
    navigation.navigate('SelectPlayer');
  }, [navigation]);

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onEndSession = useDebounce(
    async () => {
      queryClient.removeQueries({
        queryKey: [...playerSessionKeys.all, playerSessionKeys.summaryList],
      });
      navigation.push('SessionSummary');
    },
    1000,
    { leading: true },
  );

  return (
    <PlayerListTemplateV2.Root
      listLogic={listPlayerLogic}
      loadingText={STRINGS.player.listPlayers.loadingPlayers}
      skipHideActionButtonOnScroll>
      <PlayerListTemplateV2.Header>
        <AppHeader backgroundColor="white" style={styles.header}>
          <AppHeader.Left>
            <TouchableOpacity activeOpacity={0.6} onPress={onGoBack}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={scale(24)} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.playerSession.headerOverviewSession.back}
                </Text>
              </Stack>
            </TouchableOpacity>
          </AppHeader.Left>

          <AppHeader.Title>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 14px'
                  : 'Font Montserrat/Subtitle/Subtitle 2 Bold 12px'
              }
              transform="uppercase"
              fontWeight={isTablet ? undefined : '800'}
              style={styles.headerTitle}>
              {STRINGS.playerSession.headerOverviewSession.title}
            </Text>
          </AppHeader.Title>

          <AppHeader.Right>
            <TouchableOpacity hitSlop={hitSlop} activeOpacity={0.7} onPress={onEndSession}>
              <Stack
                direction="row"
                justify="flex-end"
                align="center"
                gap={scale(6)}
                width={scale(50)}>
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.playerSession.headerOverviewSession.end}
                </Text>
                <SvgIcon name="IcStop" size={scale(16)} color={colors.Blue} />
              </Stack>
            </TouchableOpacity>
          </AppHeader.Right>
        </AppHeader>
      </PlayerListTemplateV2.Header>

      <PlayerListTemplateV2.Toolbar
        titleResultsSummary={STRINGS.playerSession.headerOverviewSession.trackingPlayers(
          totalSessionRoster,
        )}>
        <PlayerListTemplateV2.SearchBar />
        <PlayerListTemplateV2.Filter />
        <PlayerListTemplateV2.Pagination />
        <PlayerListTemplateV2.ActionButton
          mobile={
            <AddTrackingButtonMobile
              numberPlayersTracked={totalSessionRoster}
              onPress={onSelectPlayer}
            />
          }
          tablet={
            <AddTrackingButtonTablet
              numberPlayersTracked={totalSessionRoster}
              onPress={onSelectPlayer}
            />
          }
        />
      </PlayerListTemplateV2.Toolbar>

      <PlayerListTemplateV2.List
        renderItem={renderItem}
        // drawDistance={2000}
        numberOfColumns={{ phone: 1, tablet: 2 }}
        gap={scale({ phone: 12, tablet: 16 })}
        emptyText={STRINGS.playerSession.headerOverviewSession.noPlayers}
      />
    </PlayerListTemplateV2.Root>
  );
}

const useStyles = createStyles(() => ({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['10'],
  },
  headerTitle: {
    color: colors.Blue,
  },
  backText: {
    color: colors.Blue,
  },
  slideLabel: {
    color: colors['10'],
  },
}));
